/**
 * Product Media Gallery Section Script
 *
 * Handles Flickity carousel (mobile), GLightbox (images/videos),
 * model-viewer loading, and image/video lazy loading for the
 * combined product media gallery section.
 */

document.addEventListener('DOMContentLoaded', () => {
  // --- Utility Functions ---
  if (!('IntersectionObserver' in window)) {
    console.warn('IntersectionObserver not supported.');
  }

  function loadScript(src, callback, errorCallback) {
    const existingScript = document.querySelector(`script[src="${src}"]`);
    if (existingScript) {
      if (existingScript.dataset.loaded === 'true') {
        if (callback) callback();
      } else {
        existingScript.addEventListener('load', () => { if (callback) callback(); }); // Ensure callback runs on load
        existingScript.addEventListener('error', errorCallback || (() => console.error(`Failed to load script: ${src}`)));
      }
      return;
    }
    const script = document.createElement('script');
    script.src = src;
    script.type = 'text/javascript';
    script.async = true;
    script.onload = () => {
        script.dataset.loaded = 'true';
        if (callback) callback();
    };
    script.onerror = errorCallback || (() => console.error(`Failed to load script: ${src}`));
    document.body.appendChild(script);
  }

  function loadStylesheet(href, callback) {
     if (document.querySelector(`link[href="${href}"]`)) {
         if (callback) callback();
         return;
     }
     const link = document.createElement('link');
     link.rel = 'stylesheet';
     link.href = href;
     link.onload = callback;
     link.onerror = () => console.error(`Failed to load stylesheet: ${href}`);
     document.head.appendChild(link);
  }

  // --- Section Initialization Logic ---
  const initializedSections = new Set();

  function initializeProductGallerySection(sectionElement) {
    const sectionId = sectionElement.dataset.sectionId;
    if (!sectionId || initializedSections.has(sectionId)) {
        return;
    }

    console.log(`Initializing Product Gallery Section: ${sectionId}`);

    const mediaList = sectionElement.querySelector('.product-media-list');
    const mediaCount = parseInt(sectionElement.dataset.mediaCount, 10) || 0;
    const noMediaMessage = sectionElement.querySelector('.no-media-message');
    const galleryId = sectionElement.dataset.galleryId || `gallery-${sectionId}`;
    const shouldAutoplayInline = sectionElement.dataset.enableVideoAutoplay === 'true';

    let flickityInstance = null;
    let glightboxInstance = null;
    let modelViewerLoaded = false;
    let inlineVideoObserver = null;

    // --- Library Initialization Functions (Scoped to this section) ---

    function initializeFlickity() {
      if (typeof Flickity === 'undefined') {
          console.log(`[${sectionId}] Flickity library not available yet.`);
          return;
      }
      // Check width again in case of resize before library loaded
      if (window.innerWidth < 750 && mediaCount > 1 && mediaList && !flickityInstance) {
        console.log(`[${sectionId}] Initializing Flickity...`);
        flickityInstance = new Flickity(mediaList, {
          cellAlign: 'center', contain: true, wrapAround: true, pageDots: true,
          prevNextButtons: false, initialIndex: 0, accessibility: true,
          cellSelector: '.product-media-list__item'
        });
        flickityInstance.on('ready', () => {
            console.log(`[${sectionId}] Flickity initialized.`);
            flickityInstance.resize();
        });
      } else if (flickityInstance && window.innerWidth >= 750) {
         // This case might be handled by resize listener, but good to double check
         console.log(`[${sectionId}] Destroying Flickity (redundant check).`);
         flickityInstance.destroy();
         flickityInstance = null;
      }
    }

    function destroyFlickity() {
        if (flickityInstance) {
            console.log(`[${sectionId}] Destroying Flickity via resize.`);
            flickityInstance.destroy();
            flickityInstance = null;
        }
    }

    function initializeGLightbox() {
        if (typeof GLightbox === 'undefined') {
            console.log(`[${sectionId}] GLightbox library not available yet.`);
            return;
        }
        if (!glightboxInstance) {
            console.log(`[${sectionId}] Attempting to initialize GLightbox...`);
            glightboxInstance = GLightbox({
                selector: `[data-section-id="${sectionId}"] .glightbox-item`,
                gallery: galleryId,
                touchNavigation: true,
                zoomable: true,
                closeOnOutsideClick: true,
                loop: false,
                autoplayVideos: true,
                skin: 'clean',
                closeButton: true,
            });
            console.log(`[${sectionId}] GLightbox initialized.`);

            glightboxInstance.on('slide_after_load', (data) => {
              const slide = data.slideNode;
              if (slide) {
                const videoElements = slide.querySelectorAll('video');
                if (videoElements.length > 0) {
                    videoElements.forEach(video => {
                      if (video) {
                        if (!video.muted) video.muted = true;
                        setTimeout(() => {
                            const playPromise = video.play();
                            if (playPromise !== undefined) {
                                playPromise.catch(error => console.warn(`[${sectionId}] GLightbox Manual play() failed:`, error.message));
                            }
                        }, 100);
                      }
                    });
                }
              }
            });
        }
    }


    function loadModelViewerScript(callback) {
        if (modelViewerLoaded) { if (callback) callback(); return; }
        console.log('Loading model-viewer script...');
        const modelViewerUrl = 'https://cdn.shopify.com/shopifycloud/model-viewer/v1.12/model-viewer.js';
        loadScript(modelViewerUrl, () => {
            modelViewerLoaded = true;
            console.log('model-viewer script loaded successfully.');
            if (callback) callback();
        }, () => console.error('Failed to load model-viewer script'));
    }

    function loadAndInitModelViewer(mediaId) {
        const template = sectionElement.querySelector(`#ModelTemplate-${mediaId}`);
        const button = sectionElement.querySelector(`[id="productModelModalOpen-${sectionId}-${mediaId}"]`);
        const itemContainer = button?.closest('.product-media-item');
        if (!template || !button || !itemContainer) { console.error(`[${sectionId}] Could not find elements for model ${mediaId}`); return; }
        loadModelViewerScript(() => {
            if (itemContainer.querySelector('model-viewer')) return;
            console.log(`[${sectionId}] Initializing model viewer for media ID: ${mediaId}`);
            const modelViewerElement = template.content.firstElementChild.cloneNode(true);
            const previewImage = itemContainer.querySelector('img');
            if (previewImage) previewImage.remove();
            button.remove();
            itemContainer.appendChild(modelViewerElement);
        });
    }

    function initializeInlineVideoObserver() {
        if (inlineVideoObserver) inlineVideoObserver.disconnect();
        const videosToObserve = sectionElement.querySelectorAll('video.product-video[data-src]');
        if (videosToObserve.length === 0) return;

        inlineVideoObserver = new IntersectionObserver((entries, observer) => {
            entries.forEach(entry => {
                const video = entry.target;
                if (entry.isIntersecting) {
                    if (!video.currentSrc && video.dataset.src) {
                        console.log(`[${sectionId}] Loading inline video src:`, video.dataset.src);
                        video.src = video.dataset.src;
                        video.load();
                    }
                    if (shouldAutoplayInline) {
                        if (!video.muted) video.muted = true;
                        const playPromise = video.play();
                        if (playPromise !== undefined) {
                            playPromise.catch(error => {
                                console.warn(`[${sectionId}] Inline video autoplay failed:`, error.message);
                            });
                        }
                    }
                } else {
                    video.pause();
                }
            });
        }, { threshold: 0.5 });

        videosToObserve.forEach(video => inlineVideoObserver.observe(video));
        console.log(`[${sectionId}] Intersection observer set up for ${videosToObserve.length} inline videos.`);
    }


    // --- Initial Setup for this section ---

    // Check initial state for Flickity
    const needsFlickityInitially = window.innerWidth < 750 && mediaCount > 1;
    if (needsFlickityInitially) {
        console.log(`[${sectionId}] Loading Flickity CSS & JS immediately for mobile...`);
        loadStylesheet('https://unpkg.com/flickity@2/dist/flickity.min.css', () => {
             loadScript('https://unpkg.com/flickity@2/dist/flickity.pkgd.min.js', initializeFlickity);
        });
    }

    // Load GLightbox and Model Viewer scripts (conditionally needed later)
    // We trigger loading early via lazyLoadContent now, initiated by IntersectionObserver
    // const needsGLightbox = sectionElement.querySelector('.glightbox-item');
    // const needsModelViewer = sectionElement.querySelector('.model-load-button');
    // if (needsGLightbox) { ... } // Loading handled in lazyLoadContent
    // if (needsModelViewer) { ... } // Loading handled in lazyLoadContent


    // --- Lazy Loading Content (Images) and Triggering Further Inits ---
    function lazyLoadContent() {
        // Prevent running multiple times if observer triggers quickly
        if (sectionElement.classList.contains('content-loaded')) return;

        console.log(`[${sectionId}] lazyLoadContent triggered by intersection.`);
        sectionElement.classList.add('content-loaded'); // Mark as triggered

        // Lazy load images
        const lazyImages = sectionElement.querySelectorAll('img.lazyload');
        lazyImages.forEach(img => {
            if (img.dataset.src) {
                img.src = img.dataset.src;
                img.onload = () => { img.classList.remove('lazyload'); }
                img.onerror = () => console.error('Image failed to load:', img.dataset.src);
            }
        });

        // Load remaining libraries if needed
        const needsGLightbox = sectionElement.querySelector('.glightbox-item');
        const needsModelViewer = sectionElement.querySelector('.model-load-button');
        const needsInlineVideoHandling = sectionElement.querySelector('video.product-video');

        if (needsGLightbox) {
            console.log(`[${sectionId}] Loading GLightbox CSS & JS...`);
             loadStylesheet('https://cdn.jsdelivr.net/npm/glightbox/dist/css/glightbox.min.css', () => {
                 loadScript('https://cdn.jsdelivr.net/npm/glightbox/dist/js/glightbox.min.js', initializeGLightbox);
             });
        }

        if (needsModelViewer) {
            loadModelViewerScript(); // Preload script if needed
        }

        if (needsInlineVideoHandling) {
            initializeInlineVideoObserver(); // Set up observer now that section is visible
        }

        sectionElement.classList.add('is-initialized'); // Mark fully initialized
        console.log(`[${sectionId}] Section marked as initialized.`);
        initializedSections.add(sectionId);
    }

    // --- Event Listeners ---
    const observer = new IntersectionObserver((entries, obs) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          console.log(`[${sectionId}] Section intersected.`);
          lazyLoadContent(); // Trigger content loading and further inits
          obs.unobserve(entry.target);
        }
      });
    }, { rootMargin: '150px 0px' }); // Keep some root margin

    if (mediaCount === 0 && noMediaMessage) {
        noMediaMessage.style.display = 'block';
        sectionElement.classList.add('is-initialized');
        initializedSections.add(sectionId);
    } else if (mediaList) {
        observer.observe(sectionElement); // Observe the section
    } else {
        console.error(`[${sectionId}] Product media list element not found.`);
        sectionElement.classList.add('is-initialized');
        initializedSections.add(sectionId);
    }

    sectionElement.addEventListener('click', function(event) {
        const modelButton = event.target.closest('.model-load-button');
        if (modelButton && modelButton.dataset.mediaId) {
            event.preventDefault();
            console.log(`[${sectionId}] Model load button clicked.`);
            loadAndInitModelViewer(modelButton.dataset.mediaId);
            return;
        }
    });

    // Handle resize events
    let resizeTimeout;
    window.addEventListener('resize', () => {
        clearTimeout(resizeTimeout);
        resizeTimeout = setTimeout(() => {
             // Check if Flickity needs destroying or initializing on resize
             if (window.innerWidth < 750) {
                 // If mobile, ensure Flickity is initialized (if needed and not already)
                 // Need to load script first if it wasn't loaded initially
                 if (!flickityInstance && mediaCount > 1) {
                     loadStylesheet('https://unpkg.com/flickity@2/dist/flickity.min.css', () => {
                         loadScript('https://unpkg.com/flickity@2/dist/flickity.pkgd.min.js', initializeFlickity);
                     });
                 } else if (flickityInstance) {
                     flickityInstance.resize(); // Just resize if already exists
                 }
             } else {
                 // If desktop, ensure Flickity is destroyed
                 destroyFlickity();
             }
             // GLightbox instance persists and handles its own resize generally
        }, 250);
    });
  } // End initializeProductGallerySection

  // --- Initialize all gallery sections on the page ---
  const gallerySections = document.querySelectorAll('[data-section-type="product-gallery"]');
  console.log(`Found ${gallerySections.length} product gallery sections.`);
  gallerySections.forEach(section => {
    initializeProductGallerySection(section);
  });

}); // End DOMContentLoaded
